import {
  type ElementType,
  type Ref,
  type ReactElement,
  forwardRef,
  useContext,
  useSyncExternalStore,
} from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { Container } from '../../layouts/container/Container.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import type { ValidationError } from '../validation/types.js';
import { FormContext } from './context/FormContext.js';
import { getFormById, subscribeToFormById } from './runtime/formRegistry.js';

/**
 * Own props for `FormMessages`, independent of the wrapper element.
 * @internal
 */
export type FormMessagesOwnProps = StylingProps &
  DataTestId &
  BehaviorTrackingProps & {
    /**
     * Associates with a `<Form>` by id when rendered outside its subtree (same
     * pattern as a native `<control form="…">`). Reads the nearest `<Form>` via
     * context when omitted.
     */
    form?: string;
  };

/**
 * Props for `FormMessages`. Defaults to a `Container` wrapper; pass `as` to
 * swap the wrapper element and take full control of layout and styling.
 * @internal
 */
export type FormMessagesProps<E extends ElementType = typeof Container> =
  PolymorphicComponentProps<E, FormMessagesOwnProps>;

const EMPTY_MESSAGES: ValidationError[] = [];
const noopSubscribe = () => () => undefined;
const getEmptyMessages = () => EMPTY_MESSAGES;

/**
 * Renders the form-wide errors (`FormValidationError`) of the enclosing — or
 * `form`-associated — `<Form>`. Field errors render in each field's
 * `FormFieldMessages`, never here.
 *
 * By default wraps errors in a `Container` with `color="critical"` and
 * `variant="default"`. Pass `as` to replace the wrapper entirely and take full
 * control of layout and styling (e.g. `as={Flex}`).
 * @internal
 */
export const FormMessages: <E extends ElementType = typeof Container>(
  props: FormMessagesProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: FormMessagesProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      as,
      form,
      className,
      style,
      'data-testid': dataTestId,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, extraProps] =
      useBehavioralTrackingProps(remainingProps);

    const context = useContext(FormContext);
    const formMessagesId = context?.formMessagesId;
    const byIdRuntime = useSyncExternalStore(
      form ? (listener) => subscribeToFormById(form, listener) : noopSubscribe,
      () => (form ? getFormById(form) : undefined),
    );
    const runtime = context?.runtime ?? byIdRuntime;

    const messages = useSyncExternalStore(
      runtime?.subscribe ?? noopSubscribe,
      runtime?.getFormMessages ?? getEmptyMessages,
    );

    if (messages.length === 0) {
      return null;
    }

    const items = messages.map((error) => (
      <BaseItem
        key={`${error.validityStateKey.toString()}:${error.message}`}
        variant="error"
      >
        {error.message}
      </BaseItem>
    ));

    const commonProps = {
      id: formMessagesId,
      'aria-live': 'polite' as const,
      className,
      style,
      'data-testid': dataTestId,
      ...behavioralTrackingProps,
    };

    if (as !== undefined && as !== null) {
      const Wrapper = as as ElementType;
      return (
        <Wrapper
          ref={forwardedRef}
          {...(extraProps as object)}
          {...commonProps}
        >
          {items}
        </Wrapper>
      );
    }

    return (
      <Container
        ref={forwardedRef as unknown as Ref<HTMLDivElement>}
        color="critical"
        variant="default"
        padding={8}
        {...(extraProps as object)}
        {...commonProps}
      >
        <Flex flexDirection="column" gap={4}>
          {items}
        </Flex>
      </Container>
    );
  },
);

(FormMessages as typeof FormMessages & { displayName: string }).displayName =
  'FormMessages';
